/**
 * Apply a manual Grade to the brushed interval on the dataset explore map.
 *
 * The tagger GRADE row is the record 2_processing reapplies. apply_manual_grade.py writes that
 * grade into the processed parquet now, so the following 0_map.py rebuild paints it. Period,
 * phase and bin-10 removal is started and not awaited — the map modal waits on map.py only.
 */
import { apiEndpoints } from "@config/env";
import { persistantStore } from "../store/persistantStore";
import { user } from "../store/userStore";
import { hasProjectWritePermission } from "../utils/userPermissions";
import { deleteData, getData, postData } from "../utils/global";
import { debug, warn } from "../utils/console";
import {
  INTERACTIVE_JOB_PRIORITY,
  runScriptJobForUi,
  type LegacyExecuteScriptResponse,
} from "../utils/scriptJobs";
import {
  downgradeEventIds,
  DOWNGRADE_EVENT_TYPES,
  GRADE_ZERO_MANEUVER_EVENT_TYPES,
  gradeZeroManeuverEventIds,
  isAssignableGrade,
  isDowngradeGrade,
  type TimedEvent,
} from "../utils/mapGradeSelection";

export const MAP_GRADE_APPLIED_EVENT = "racesight-map-grade-applied";

export interface GradeJobHandle {
  jobId: string;
  processId: string;
}

async function runJob(
  scriptName: string,
  parameters: Record<string, unknown>,
  onQueued: (handle: GradeJobHandle) => void
): Promise<LegacyExecuteScriptResponse> {
  const className = String(parameters.class_name ?? "");
  const handle = await runScriptJobForUi(
    {
      class_name: className,
      script_name: scriptName,
      parameters,
      project_id: parameters.project_id as string | number,
      priority: INTERACTIVE_JOB_PRIORITY,
    },
    { showToast: false }
  );
  onQueued({ jobId: handle.job_id, processId: handle.process_id });
  return handle.completion;
}

function scriptFailed(result: LegacyExecuteScriptResponse, fallback: string): string | null {
  if (result?.success && result.data?.script_succeeded !== false) {
    return null;
  }
  const lines = [...(result?.data?.error_lines ?? []), ...(result?.data?.output_lines ?? [])];
  const specific = lines.find((line) => line && !/^Script failed with return code \d+$/.test(line));
  return specific || result?.message || fallback;
}

/** Fire-and-forget. Failures are logged; they must not block the map rebuild. */
export function removeDowngradedEventsInBackground(
  interval: { start: string; end: string },
  grade: number
): void {
  void removeDowngradedEvents(interval, grade).catch((err: unknown) => {
    warn("[mapGrade] background event removal failed", err);
  });
}

async function removeDowngradedEvents(
  interval: { start: string; end: string },
  grade: number
): Promise<void> {
  const className = String(persistantStore.selectedClassName?.() ?? "").trim();
  const projectId = Number(persistantStore.selectedProjectId?.());
  const datasetId = Number(persistantStore.selectedDatasetId?.());
  if (!className || !Number.isFinite(projectId) || projectId <= 0 || !Number.isFinite(datasetId) || datasetId <= 0) {
    warn("[mapGrade] skipping event removal: missing dataset context");
    return;
  }

  const eventTypes: string[] = [...DOWNGRADE_EVENT_TYPES];
  if (grade === 0) {
    eventTypes.push(...GRADE_ZERO_MANEUVER_EVENT_TYPES);
  }

  const lists = await Promise.all(
    eventTypes.map(async (eventType) => {
      const url =
        `${apiEndpoints.app.events}/info?class_name=${encodeURIComponent(className)}` +
        `&project_id=${encodeURIComponent(String(projectId))}` +
        `&dataset_id=${encodeURIComponent(String(datasetId))}` +
        `&event_type=${encodeURIComponent(eventType)}`;
      const res = await getData(url);
      if (!res?.success || !Array.isArray(res.data)) {
        return [] as TimedEvent[];
      }
      return (res.data as TimedEvent[]).map((row) => ({ ...row, event_type: row.event_type ?? eventType }));
    })
  );

  const rows = lists.flat();
  const ids = [
    ...downgradeEventIds(rows, interval.start, interval.end),
    ...(grade === 0 ? gradeZeroManeuverEventIds(rows, interval.start, interval.end) : []),
  ];
  if (ids.length === 0) {
    debug("[mapGrade] no events inside the downgrade");
    return;
  }

  const res = await deleteData(`${apiEndpoints.admin.events}`, {
    class_name: className,
    project_id: projectId,
    dataset_id: datasetId,
    events: ids,
  });
  if (!res?.success) {
    warn("[mapGrade] could not remove downgraded events", res?.message);
    return;
  }
  debug("[mapGrade] removed events inside the downgrade", { count: ids.length, grade });
}

export interface ApplyMapGradeArgs {
  grade: number;
  interval: { start: string; end: string };
  onJob: (scriptName: string, handle: GradeJobHandle) => void;
}

/**
 * Save the grade, write the parquet, start event cleanup when this is a downgrade, then rebuild
 * the map. Resolves after 0_map.py finishes.
 */
export async function applyMapGrade(args: ApplyMapGradeArgs): Promise<void> {
  if (!isAssignableGrade(args.grade)) {
    throw new Error(`Grade ${args.grade} is not assignable`);
  }
  const className = String(persistantStore.selectedClassName?.() ?? "").trim();
  const projectId = Number(persistantStore.selectedProjectId?.());
  const datasetId = Number(persistantStore.selectedDatasetId?.());
  const sourceName = String(persistantStore.selectedSourceName?.() ?? "").trim();
  const dateRaw = String(persistantStore.selectedDate?.() ?? "");
  const datasetDate =
    typeof window !== "undefined"
      ? String((window as Window & { datasetDate?: string }).datasetDate ?? "")
      : "";
  if (!className || !Number.isFinite(projectId) || projectId <= 0 || !Number.isFinite(datasetId) || datasetId <= 0) {
    throw new Error("Missing class, project or dataset");
  }
  if (!hasProjectWritePermission(user(), projectId)) {
    throw new Error("You do not have permission to set a grade on this project");
  }
  if (!sourceName) {
    throw new Error("Missing source name");
  }

  const saved = await postData(apiEndpoints.app.userEvents, {
    project_id: projectId,
    event_type: "GRADE",
    tags: { Grade: args.grade, Source_name: sourceName },
    start_time: args.interval.start,
    end_time: args.interval.end,
    focus_time: args.interval.start,
    date: args.interval.start.slice(0, 10),
  });
  if (!saved?.success) {
    throw new Error(saved?.message || "Could not save the grade");
  }

  const date = (datasetDate || dateRaw || args.interval.start.slice(0, 10)).replace(/[-/]/g, "");
  const shared = {
    class_name: className,
    project_id: String(projectId),
    dataset_id: String(datasetId),
    date,
    source_name: sourceName,
    start_time: null as string | null,
    end_time: null as string | null,
    timezone: "UTC",
    batch: true,
    verbose: false,
  };

  const gradeResult = await runJob(
    "apply_manual_grade.py",
    {
      ...shared,
      start_time: args.interval.start,
      end_time: args.interval.end,
      grade: args.grade,
      batch: false,
    },
    (handle) => args.onJob("apply_manual_grade.py", handle)
  );
  const gradeError = scriptFailed(gradeResult, "Could not write the grade into the processed data");
  if (gradeError) {
    throw new Error(gradeError);
  }

  if (isDowngradeGrade(args.grade)) {
    removeDowngradedEventsInBackground(args.interval, args.grade);
  }

  const mapResult = await runJob("0_map.py", shared, (handle) => args.onJob("0_map.py", handle));
  const mapError = scriptFailed(mapResult, "Map rebuild failed");
  if (mapError) {
    throw new Error(mapError);
  }
}
